import { CHANNELS } from "../../shared/channels";
import { RoutineDraft, RoutineSchedule, RoutineStatus } from "../../shared/types";
import { call, listen } from "../bridge";
import { byId, flashToast, renderList } from "../dom";
import { t } from "../i18n";
import { isViewActive, onViewShown, showView } from "../navigation";
import { buildRoutineItem, localDate } from "./routine_item";
import { fillSharedBox, onSharedChange, readSharedAll } from "../shares";

// repeating routines: what is on today, the rest, and a small editor

let editing: RoutineStatus | null = null;

function kindSelect(): HTMLSelectElement {
  return byId<HTMLSelectElement>("routines-kind");
}

function pickedDays(): number[] {
  return [...byId("routines-days").querySelectorAll<HTMLElement>(".chip.active")].map((chip) => Number(chip.dataset.day));
}

function showKindFields(): void {
  const kind = kindSelect().value;
  const count = byId<HTMLInputElement>("routines-count");
  const label = byId("routines-count-label");
  count.hidden = !["every", "perWeek", "monthly"].includes(kind);
  label.hidden = count.hidden;
  label.textContent = kind === "every" ? t("days apart") : kind === "perWeek" ? t("times a week, any days") : t("day of the month");
  count.max = kind === "perWeek" ? "7" : kind === "monthly" ? "31" : "365";
  byId("routines-days").hidden = kind !== "weekdays";
}

function readSchedule(): RoutineSchedule {
  const count = Number(byId<HTMLInputElement>("routines-count").value) || 1;
  switch (kindSelect().value) {
    case "weekdays":
      return { kind: "weekdays", days: pickedDays() };
    case "every":
      return { kind: "every", days: count };
    case "perWeek":
      return { kind: "perWeek", times: count };
    case "monthly":
      return { kind: "monthly", day: count };
    default:
      return { kind: "daily" };
  }
}

function fillSchedule(schedule: RoutineSchedule): void {
  kindSelect().value = schedule.kind;
  const count = byId<HTMLInputElement>("routines-count");
  if (schedule.kind === "every") count.value = String(schedule.days);
  else if (schedule.kind === "perWeek") count.value = String(schedule.times);
  else if (schedule.kind === "monthly") count.value = String(schedule.day);
  else count.value = "2";
  const days = schedule.kind === "weekdays" ? schedule.days : [];
  byId("routines-days").querySelectorAll<HTMLElement>(".chip").forEach((chip) => chip.classList.toggle("active", days.includes(Number(chip.dataset.day))));
  showKindFields();
}

export function openRoutineEditor(item: RoutineStatus | null): void {
  editing = item;
  const routine = item?.routine;
  byId<HTMLInputElement>("routines-title").value = routine?.title || "";
  byId<HTMLInputElement>("routines-at").value = routine?.remindAt || "";
  byId<HTMLInputElement>("routines-note").value = routine?.note || "";
  byId<HTMLInputElement>("routines-paused").checked = Boolean(routine?.paused);
  byId("routines-delete").hidden = !routine;
  byId("routines-status").textContent = routine?.key ? t("added by another part of chix, ticked off there too") : "";
  fillSchedule(routine?.schedule || { kind: "daily" });
  const form = byId<HTMLFormElement>("routines-form");
  form.hidden = false;
  form.scrollIntoView({ block: "nearest" });
  byId<HTMLInputElement>("routines-title").focus();
}

function closeEditor(): void {
  editing = null;
  byId<HTMLFormElement>("routines-form").hidden = true;
}

function askToNotify(): void {
  // desktop without a cloud link gets windows toasts from the main process instead
  if (window.bridge || typeof Notification === "undefined" || Notification.permission !== "default") return;
  void Notification.requestPermission();
}

async function save(event: Event): Promise<void> {
  event.preventDefault();
  const draft: RoutineDraft = {
    id: editing?.routine.id,
    title: byId<HTMLInputElement>("routines-title").value,
    note: byId<HTMLInputElement>("routines-note").value,
    remindAt: byId<HTMLInputElement>("routines-at").value,
    schedule: readSchedule(),
    paused: byId<HTMLInputElement>("routines-paused").checked,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
  try {
    await call(CHANNELS.routinesSave, draft);
    if (draft.remindAt) askToNotify();
    closeEditor();
    void reload();
  } catch (error) {
    byId("routines-status").textContent = error instanceof Error ? error.message : String(error);
  }
}

async function remove(): Promise<void> {
  if (!editing || !window.confirm(t("delete {title} and its history?", { title: editing.routine.title }))) return;
  await call(CHANNELS.routinesRemove, editing.routine.id);
  closeEditor();
  void reload();
}

async function reload(): Promise<void> {
  const items = await call<RoutineStatus[]>(CHANNELS.routinesList, localDate());
  const today = items.filter((item) => item.due && !item.routine.paused);
  const later = items.filter((item) => !today.includes(item));
  today.sort((first, second) => Number(first.done) - Number(second.done));
  renderList(byId("routines-today"), today, (item) => buildRoutineItem(item, openRoutineEditor));
  renderList(byId("routines-later"), later, (item) => buildRoutineItem(item, openRoutineEditor));
  byId("routines-today-empty").hidden = today.length > 0 || items.length === 0;
  byId("routines-empty").hidden = items.length > 0;
  await reloadShared();
}

async function reloadShared(): Promise<void> {
  const people = await readSharedAll<RoutineStatus[]>("routines", []);
  fillSharedBox(byId("routines-shared"), people, "routines", (item) => buildRoutineItem(item, null), t("no routines"));
}

function onDue(payload: { title?: string; view?: string }): void {
  const title = payload.title || t("routine");
  const view = payload.view || "routines";
  if (typeof Notification !== "undefined" && Notification.permission === "granted" && document.visibilityState !== "visible") {
    const note = new Notification(t("ChiX routine"), { body: title, icon: "assets/icon.png" });
    note.onclick = () => {
      window.focus();
      showView(view);
    };
    return;
  }
  flashToast(t("time for {title}", { title }));
}

export function setupRoutinesView(): void {
  byId("routines-new").addEventListener("click", () => openRoutineEditor(null));
  byId("routines-cancel").addEventListener("click", closeEditor);
  byId("routines-delete").addEventListener("click", () => void remove());
  byId<HTMLFormElement>("routines-form").addEventListener("submit", save);
  kindSelect().addEventListener("change", showKindFields);
  byId("routines-days").querySelectorAll<HTMLElement>(".chip").forEach((chip) => {
    chip.addEventListener("click", () => chip.classList.toggle("active"));
  });
  listen(CHANNELS.routinesUpdated, () => {
    if (isViewActive("routines")) void reload();
  });
  listen(CHANNELS.routinesDue, onDue);
  onSharedChange("routines", () => {
    if (isViewActive("routines")) void reloadShared();
  });
  onViewShown("routines", () => void reload().catch((error) => flashToast(String(error))));
}
